import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

// --- Utility Functions for Date Management ---
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year, month) => {
  return new Date(year, month, 1).getDay();
};

const formatDateForDisplay = (date) => {
  return new Date(date).toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isSameDay = (d1, d2) => {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const toProperCase = (text = "") => {
  if (!text) return "";
  const lowerCaseWords = new Set([
    "of", "and", "or", "as", "in", "on", "at", "to", "for", "by", "with", 
    "via", "vs", "etc", "the", "a", "an",
  ]);
  return text
    .toLowerCase()
    .split(" ")
    .map((word, index, arr) => {
      if (index !== 0 && index !== arr.length - 1 && lowerCaseWords.has(word))
        return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" })
    .format(amount || 0)
    .replace("NGN", "₦");

const formatDate = (dateString) =>
  !dateString
    ? "N/A"
    : new Date(dateString).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

const getExpiryDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  date.setFullYear(date.getFullYear() + 1);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// --- Styles ---
const styles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: inline-block;
    height: 1em;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
    width: 100%;
  }
  .skeleton-circle {
    display: inline-block;
    height: 50px;
    width: 50px;
    border-radius: 8px;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }
  .img-zoom-hover {
    transition: transform 0.3s ease;
    cursor: zoom-in;
  }
  .img-zoom-hover:hover {
    transform: scale(1.02);
  }
  .calendar-day {
    height: 80px;
    transition: 0.2s;
    cursor: pointer;
  }
  .calendar-day:hover {
    background-color: #f1faff !important;
    border-color: #009ef7 !important;
  }
  .day-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
  }
  .stats-card {
    transition: all 0.2s ease;
  }
  .stats-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075);
  }
`;

// --- Main Component: Calendar Ads Management ---
const CalendarAdsManagement = () => {
  const BASE_URL = "https://adera-web-api.azurewebsites.net/v1";
  
  // ==================== CALENDAR STATE ====================
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allAds, setAllAds] = useState([]);
  const [filteredAds, setFilteredAds] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, unsuccessful: 0 });

  // ==================== AD MANAGEMENT STATE ====================
  const [lookups, setLookups] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);

  // ==================== MODAL STATES ====================
  const [showPermitModal, setShowPermitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [permitData, setPermitData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [originalLookup, setOriginalLookup] = useState(null);

  // ==================== EDIT FORM STATE ====================
  const [editForm, setEditForm] = useState({
    id: "",
    clientId: "",
    title: "",
    description: "",
    address: "",
    price: 0,
    status: "",
    measurement: 1,
    time: 1,
    lookUpId: "",
    lookup: {
      location: { name: "" },
      size: { name: "" },
      type: { name: "" },
      category: { name: "" },
    },
    location: { lga: "", longitude: 0, latitude: 0, altitude: 0 },
    client: {
      type: "Individual",
      company: { name: "", address: "", phoneNumber: "", email: "", type: "" },
      individual: { firstName: "", lastName: "", gender: "", phoneNumber: "", email: "" },
    },
    imageFile: null,
    imagePreview: null,
  });

  // ==================== TOKEN & HELPERS ====================
  const getToken = () => localStorage.getItem("token") || "demo-token";

  const getClientName = (client) => {
    if (!client) return "Unknown";
    if (client.company) return toProperCase(client.company.name);
    if (client.individual)
      return toProperCase(`${client.individual.firstName} ${client.individual.lastName}`);
    return "Unknown Entity";
  };

  const getClientPhone = (client) => {
    if (!client) return "N/A";
    if (client.company) return client.company.phoneNumber || "N/A";
    if (client.individual) return client.individual.phoneNumber || "N/A";
    return "N/A";
  };

  const getClientEmail = (client) => {
    if (!client) return "N/A";
    if (client.company) return client.company.email || "N/A";
    if (client.individual) return client.individual.email || "N/A";
    return "N/A";
  };

  // ==================== LOOKUP OPTIONS ====================
  const lookupOptions = useMemo(() => {
    if (!lookups || lookups.length === 0)
      return { sizes: [], zones: [], types: [], categories: [] };
    
    const uniqueFilter = (arr) => [...new Set(arr.filter(Boolean))];
    const sortSizes = (sizes) => {
      return sizes.sort((a, b) => {
        const getNum = (str) => {
          const match = str.match(/(\d+(\.\d+)?)/);
          return match ? parseFloat(match[0]) : Infinity;
        };
        const numA = getNum(a);
        const numB = getNum(b);
        if (numA === numB) return a.localeCompare(b);
        return numA - numB;
      });
    };

    return {
      sizes: sortSizes(uniqueFilter(lookups.map((l) => l.size?.name))),
      zones: uniqueFilter(lookups.map((l) => l.location?.name)).sort(),
      types: uniqueFilter(lookups.map((l) => l.type?.name)).sort(),
      categories: uniqueFilter(lookups.map((l) => l.category?.name)).sort(),
    };
  }, [lookups]);

  // ==================== DATA FETCHING ====================
  const fetchGlobals = async () => {
    try {
      const token = getToken();
      const lookupRes = await fetch(`${BASE_URL}/admin/ad/lookup?all=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const lookupData = await lookupRes.json();
      if (lookupData.ok)
        setLookups(lookupData.data.pageData || lookupData.data || []);
    } catch (error) {
      console.error("Error fetching lookups:", error);
    }
  };

  const fetchAllAds = async () => {
    setCalendarLoading(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${BASE_URL}/admin/payment?page=1&count=5000&all=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await response.json();

      if (result.ok) {
        const data = result.data.pageData || result.data || [];
        setAllAds(data);
        filterAdsByDate(data, selectedDate);
      }
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setCalendarLoading(false);
    }
  };

  // ==================== FILTER LOGIC ====================
  const filterAdsByDate = (ads, date) => {
    const dayAds = ads.filter((ad) => {
      const adDate = new Date(ad.createdAt);
      return isSameDay(adDate, date);
    });

    setFilteredAds(dayAds);

    const stats = dayAds.reduce(
      (acc, curr) => {
        acc.total += curr.amount || 0;
        if (curr.status === "Successful") acc.paid++;
        else if (curr.status === "Initialize" || curr.status === "AwaitingPayment") acc.pending++;
        else acc.unsuccessful++;
        return acc;
      },
      { total: 0, paid: 0, pending: 0, unsuccessful: 0 }
    );
    setStats(stats);
  };

  // ==================== EXPORT FUNCTION ====================
  const handleExport = async () => {
    setExporting(true);
    try {
      const dataToExport = filteredAds.map((payment) => ({
        "Business Name": getClientName(payment.ad?.client),
        "Invoice Code": payment.code,
        "Ad Title": toProperCase(payment.ad?.title || "-"),
        "Ad Type": toProperCase(payment.ad?.lookup?.type?.name || "-"),
        "Party Type": toProperCase(payment.ad?.lookup?.category?.name || "-"),
        "Zone": toProperCase(payment.ad?.lookup?.location?.name || "-"),
        "Faces": payment.ad?.measurement || 1,
        "Amount": payment.amount || 0,
        "Status": payment.status === "Successful" ? "Paid" : 
                  payment.status === "Initialize" ? "Pending" : 
                  payment.status === "UnSuccessful" ? "Unsuccessful" : payment.status,
        "Duration": payment.ad?.duration || "Annual",
        "Date Created": formatDate(payment.createdAt),
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Day Ads");
      XLSX.writeFile(
        workbook,
        `Ads_${formatDateForDisplay(selectedDate).replace(/\//g, "-")}.xlsx`
      );
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export.");
    } finally {
      setExporting(false);
    }
  };

  // ==================== CALENDAR NAVIGATION ====================
  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const onDateClick = (day) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    setSelectedDate(newDate);
  };

  // ==================== CALENDAR RENDER ====================
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(
      currentDate.getFullYear(),
      currentDate.getMonth()
    );
    const firstDayIndex = getFirstDayOfMonth(
      currentDate.getFullYear(),
      currentDate.getMonth()
    );

    const days = [];
    
    for (let i = 0; i < firstDayIndex; i++) {
      days.push(<div key={`empty-${i}`} className="p-4"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const thisDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        i
      );
      const isSelected = isSameDay(thisDate, selectedDate);
      const isToday = isSameDay(thisDate, new Date());
      
      const dayAds = allAds.filter(ad => isSameDay(new Date(ad.createdAt), thisDate));
      const hasAds = dayAds.length > 0;
      const paidCount = dayAds.filter(ad => ad.status === "Successful").length;
      const pendingCount = dayAds.filter(ad => ad.status === "Initialize" || ad.status === "AwaitingPayment").length;

      days.push(
        <div
          key={i}
          onClick={() => onDateClick(i)}
          className={`
            position-relative p-3 cursor-pointer text-center fw-bold rounded border 
            transition-all calendar-day
            ${isSelected ? "bg-primary text-white shadow" : "bg-light hover:bg-light-primary text-gray-800"}
            ${isToday && !isSelected ? "border-primary text-primary" : ""}
          `}
          style={{ height: "85px" }}
        >
          <span className="fs-5">{i}</span>
          
          {hasAds && (
            <div className="position-absolute bottom-0 start-0 end-0 px-1 pb-1">
              <div className="d-flex justify-content-center gap-1">
                {paidCount > 0 && (
                  <span className="badge badge-circle bg-success" style={{ width: "8px", height: "8px" }} 
                        title={`${paidCount} Paid`}></span>
                )}
                {pendingCount > 0 && (
                  <span className="badge badge-circle bg-warning" style={{ width: "8px", height: "8px" }}
                        title={`${pendingCount} Pending`}></span>
                )}
              </div>
              <span className="badge badge-light-primary mt-1" style={{ fontSize: "10px" }}>
                {dayAds.length}
              </span>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  // ==================== EDIT FUNCTIONS ====================
  const handleEditClick = (payment) => {
    const ad = payment.ad;
    if (!ad) return alert("No advertisement data found for this payment.");

    const originalLookupCombination = {
      size: ad.lookup?.size?.name || "",
      location: ad.lookup?.location?.name || "",
      type: ad.lookup?.type?.name || "",
      category: ad.lookup?.category?.name || "",
      price: ad.price || 0,
    };
    setOriginalLookup(originalLookupCombination);

    setEditForm({
      id: ad.id,
      clientId: ad.client?.id || "",
      title: ad.title || "",
      description: ad.description || "",
      address: ad.address || "",
      price: ad.price || 0,
      status: ad.status || "",
      measurement: ad.measurement || 1,
      time: ad.time || 1,
      lookUpId: ad.lookup?.id || "",
      lookup: {
        location: { name: ad.lookup?.location?.name || "" },
        size: { name: ad.lookup?.size?.name || "" },
        type: { name: ad.lookup?.type?.name || "" },
        category: { name: ad.lookup?.category?.name || "" },
      },
      location: {
        lga: ad.location?.lga || "",
        longitude: ad.location?.longitude || 0,
        latitude: ad.location?.latitude || 0,
        altitude: ad.location?.altitude || 0,
      },
      client: {
        type: ad.client?.type || "Individual",
        company: {
          name: ad.client?.company?.name || "",
          address: ad.client?.company?.address || "",
          phoneNumber: ad.client?.company?.phoneNumber || "",
          email: ad.client?.company?.email || "",
          type: ad.client?.company?.type || "",
        },
        individual: {
          firstName: ad.client?.individual?.firstName || "",
          lastName: ad.client?.individual?.lastName || "",
          gender: ad.client?.individual?.gender || "",
          phoneNumber: ad.client?.individual?.phoneNumber || "",
          email: ad.client?.individual?.email || "",
        },
      },
      imageFile: null,
      imagePreview: ad.imageUrl || null,
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "imageFile") {
      if (files && files[0]) {
        const file = files[0];
        setEditForm((prev) => ({
          ...prev,
          imageFile: file,
          imagePreview: URL.createObjectURL(file),
        }));
      }
      return;
    }
    if (name === "price") {
      setEditForm((prev) => ({ ...prev, price: parseFloat(value) || 0 }));
      return;
    }
    setEditForm((prev) => {
      const newState = JSON.parse(JSON.stringify(prev));
      const keys = name.split(".");
      let current = newState;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newState;
    });
  };

  useEffect(() => {
    if (!showEditModal || !originalLookup) return;

    const { size, location, type, category } = editForm.lookup;
    if (!size.name || !location.name || !type.name || !category.name) return;

    const normalizeString = (str) => (str || "").toString().toLowerCase().trim();

    const hasChanged =
      normalizeString(size.name) !== normalizeString(originalLookup.size) ||
      normalizeString(location.name) !== normalizeString(originalLookup.location) ||
      normalizeString(type.name) !== normalizeString(originalLookup.type) ||
      normalizeString(category.name) !== normalizeString(originalLookup.category);

    if (hasChanged) {
      const matchedLookup = lookups.find((lookup) => {
        return (
          normalizeString(lookup.size?.name) === normalizeString(size.name) &&
          normalizeString(lookup.location?.name) === normalizeString(location.name) &&
          normalizeString(lookup.type?.name) === normalizeString(type.name) &&
          normalizeString(lookup.category?.name) === normalizeString(category.name)
        );
      });

      const autoTitle = `${toProperCase(size.name)} ${toProperCase(type.name)} in ${toProperCase(location.name)}`;

      setEditForm((prev) => {
        if (
          prev.price === (matchedLookup?.price || 0) &&
          prev.title === autoTitle &&
          prev.lookUpId === (matchedLookup?.id || "")
        )
          return prev;
        return {
          ...prev,
          price: matchedLookup ? matchedLookup.price : 0,
          title: autoTitle,
          lookUpId: matchedLookup ? matchedLookup.id : "",
        };
      });
    }
  }, [
    editForm.lookup.size.name,
    editForm.lookup.location.name,
    editForm.lookup.type.name,
    editForm.lookup.category.name,
    lookups,
    showEditModal,
    originalLookup,
  ]);

  const handleSaveAd = async () => {
    setSaving(true);
    try {
      const token = getToken();

      if (editForm.clientId) {
        const clientPayload =
          editForm.client.type === "Company"
            ? {
                name: editForm.client.company.name,
                address: editForm.client.company.address,
                email: editForm.client.company.email,
                phoneNumber: editForm.client.company.phoneNumber,
                type: editForm.client.company.type || "Others",
              }
            : {
                firstName: editForm.client.individual.firstName,
                lastName: editForm.client.individual.lastName,
                email: editForm.client.individual.email,
                phoneNumber: editForm.client.individual.phoneNumber,
                gender: editForm.client.individual.gender || "Male",
              };

        const clientEndpoint =
          editForm.client.type === "Company"
            ? `${BASE_URL}/admin/client/${editForm.clientId}/company`
            : `${BASE_URL}/admin/client/${editForm.clientId}/individual`;

        const clientResponse = await fetch(clientEndpoint, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(clientPayload),
        });

        const clientResult = await clientResponse.json();
        if (!clientResult.ok) {
          alert(`Failed to update client: ${clientResult.error?.message || "Unknown error"}`);
          setSaving(false);
          return;
        }
      }

      const formData = new FormData();

      formData.append("title", editForm.title);
      formData.append("description", editForm.description);
      formData.append("address", editForm.address);
      formData.append("clientId", editForm.clientId);
      formData.append("time", editForm.time.toString());
      formData.append("measurement", editForm.measurement.toString());

      formData.append("lga", editForm.location.lga);
      formData.append("latitude", editForm.location.latitude.toString());
      formData.append("longitude", editForm.location.longitude.toString());
      formData.append("altitude", editForm.location.altitude.toString());

      if (editForm.lookUpId) {
        formData.append("lookUpId", editForm.lookUpId);
      }

      if (editForm.imageFile) formData.append("image", editForm.imageFile);

      const response = await fetch(`${BASE_URL}/admin/ad/${editForm.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await response.json();

      if (result.ok) {
        await fetchAllAds();
        setShowEditModal(false);
        setOriginalLookup(null);
        alert("Advertisement and client information updated successfully!");
      } else {
        alert(result.error?.message || "Failed to update advertisement");
      }
    } catch (e) {
      console.error("Save error:", e);
      alert("An error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAd = async () => {
    if (!showDeleteConfirm && 
        !window.confirm(`Are you sure you want to delete this advertisement? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const token = getToken();

      const response = await fetch(`${BASE_URL}/admin/ad`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          adsId: [editForm.id],
        }),
      });

      const result = await response.json();

      if (result.ok) {
        await fetchAllAds();
        setShowEditModal(false);
        setShowDeleteConfirm(false);
        setOriginalLookup(null);
        alert("Advertisement deleted successfully!");
      } else {
        alert(result.error?.message || "Failed to delete advertisement");
      }
    } catch (e) {
      console.error("Delete error:", e);
      alert("An error occurred while deleting.");
    } finally {
      setDeleting(false);
    }
  };

  const handleViewClick = (payment) => {
    const ad = payment.ad || {};
    setViewData({
      clientName: getClientName(ad.client),
      clientPhone: getClientPhone(ad.client),
      clientEmail: getClientEmail(ad.client),
      clientAddress: ad.client?.company?.address || "N/A",
      invoiceCode: payment.code,
      amount: payment.amount,
      status: payment.status,
      date: payment.createdAt,
      adTitle: ad.title,
      adType: ad.lookup?.type?.name,
      adCategory: ad.lookup?.category?.name,
      adSize: ad.lookup?.size?.name,
      adZone: ad.lookup?.location?.name,
      adAddress: ad.address,
      imageUrl: ad.imageUrl,
    });
    setShowViewModal(true);
  };

  // ==================== INITIAL LOAD ====================
  useEffect(() => {
    fetchGlobals();
    fetchAllAds();
  }, []);

  useEffect(() => {
    if (allAds.length > 0) {
      filterAdsByDate(allAds, selectedDate);
    }
  }, [selectedDate, allAds]);

  // ==================== RENDER SKELETONS ====================
  const renderSkeletons = () => {
    return Array.from({ length: 5 }).map((_, idx) => (
      <tr key={`skel-${idx}`}>
        <td>
          <div className="d-flex align-items-center">
            <div className="skeleton-circle me-3"></div>
            <div className="d-flex flex-column w-100">
              <div className="skeleton-box mb-1" style={{ width: "60%" }}></div>
              <div className="skeleton-box mb-1" style={{ width: "40%" }}></div>
              <div className="skeleton-box" style={{ width: "30%" }}></div>
            </div>
          </div>
        </td>
        <td><div className="skeleton-box" style={{ width: "70%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "80%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "60%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "50%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "30%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "40%" }}></div></td>
        <td><div className="skeleton-box" style={{ width: "60px", height: "25px" }}></div></td>
        <td className="text-end">
          <div className="d-flex justify-content-end gap-2">
            <div className="skeleton-box" style={{ width: "80px", height: "30px" }}></div>
            <div className="skeleton-box" style={{ width: "100px", height: "30px" }}></div>
          </div>
        </td>
      </tr>
    ));
  };

  // ==================== MAIN RENDER ====================
  return (
    <>
      <style>{styles}</style>
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          
          {/* ========== HEADER ========== */}
          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between">
              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/">
                  <img
                    alt="Logo"
                    src="assets/aderalogo.png"
                    className="h-60px"
                  />
                </Link>
              </div>
              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div className="d-flex align-items-stretch" id="kt_app_header_menu_wrapper">
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div className="menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2">
                      <div className="menu-item me-2">
                        <Link to="/dashboarda" className="menu-link">
                          <span className="menu-title">Home</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link to="/ad-management" className="menu-link">
                          <span className="menu-title">Advertisement</span>
                        </Link>
                      </div>
                      <div className="menu-item me-2">
                        <Link to="/agents" className="menu-link">
                          <span className="menu-title">Agents</span>
                        </Link>
                      </div>
                      <div className="menu-item here show menu-here-bg me-2">
                        <Link to="/payments" className="menu-link">
                          <span className="menu-title">Calendar View</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                  <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                    <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                    <span className="text-gray-800 fs-7 fw-bold">EDSAA Admin</span>
                  </div>
                  <div className="cursor-pointer symbol symbol-circle symbol-40px">
                    <img src={"assets/edsal.png"} alt="User" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper d-flex flex-column flex-column-fluid" id="kt_app_wrapper">
            <div className="app-container container-fluid d-flex flex-row-fluid">
              <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
                <div className="d-flex flex-column flex-column-fluid">
                  <div id="kt_app_content" className="app-content flex-column-fluid">
                    <div className="app-container container-fluid">
                      
                      {/* ========== HERO SECTION ========== */}
                      <div
                        className="d-flex flex-stack flex-wrap flex-md-nowrap card-rounded shadow p-5 p-lg-10 mb-10"
                        style={{
                          background: "linear-gradient(90deg, #20AA3E 0%, #03A588 100%)",
                        }}
                      >
                        <div className="my-2 me-5">
                          <div className="fs-2x fw-bold text-white mb-2">
                            <span className="fw-normal">📅 Day-by-Day Ad Management</span>
                          </div>
                          <div className="fs-6 text-white opacity-75">
                            Select any date on the calendar to view all registered advertisements
                          </div>
                        </div>
                      </div>

                      {/* ========== CALENDAR SECTION ========== */}
                      <div className="card mb-8 shadow-sm">
                        <div className="card-header border-0 pt-5">
                          <h3 className="card-title align-items-start flex-column">
                            <span className="card-label fw-bold fs-3 mb-1">
                              <i className="ki-outline ki-calendar fs-2 me-2 text-primary"></i>
                              Ads Calendar Sort
                            </span>
                            <span className="text-muted mt-1 fw-semibold fs-7">
                              Click on a day to view all advertisements registered on that date
                            </span>
                          </h3>
                          <div className="card-toolbar">
                            <div className="d-flex align-items-center position-relative my-1">
                              <button 
                                className="btn btn-icon btn-sm btn-light-primary me-3" 
                                onClick={prevMonth}
                              >
                                <i className="ki-outline ki-left fs-2"></i>
                              </button>
                              <span className="fw-bold fs-4 text-gray-800 w-150px text-center">
                                {currentDate.toLocaleDateString("en-US", { month: 'long', year: 'numeric' })}
                              </span>
                              <button 
                                className="btn btn-icon btn-sm btn-light-primary ms-3" 
                                onClick={nextMonth}
                              >
                                <i className="ki-outline ki-right fs-2"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="card-body py-3">
                          {/* Weekday Headers */}
                          <div className="d-grid gap-2 mb-2" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                              <div key={day} className="text-center text-muted fw-bold fs-7 text-uppercase">
                                {day}
                              </div>
                            ))}
                          </div>
                          
                          {/* Days Grid */}
                          <div className="d-grid gap-2" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
                            {renderCalendarDays()}
                          </div>
                          
                          {/* Legend */}
                          <div className="d-flex justify-content-center mt-4 pt-2 border-top">
                            <div className="d-flex align-items-center gap-4">
                              <div className="d-flex align-items-center">
                                <span className="badge badge-circle bg-success me-1" style={{ width: "10px", height: "10px" }}></span>
                                <span className="text-muted fs-7">Paid</span>
                              </div>
                              <div className="d-flex align-items-center">
                                <span className="badge badge-circle bg-warning me-1" style={{ width: "10px", height: "10px" }}></span>
                                <span className="text-muted fs-7">Pending</span>
                              </div>
                              <div className="d-flex align-items-center">
                                <span className="badge badge-light-primary me-1">123</span>
                                <span className="text-muted fs-7">Total Ads</span>
                              </div>
                              <div className="d-flex align-items-center">
                                <span className="bg-primary text-white px-2 py-1 rounded-1 me-1" style={{ width: "20px", height: "20px" }}></span>
                                <span className="text-muted fs-7">Selected Day</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ========== RESULTS SECTION ========== */}
                      <div className="card card-flush shadow-sm">
                        <div className="card-header align-items-center py-5 gap-2 gap-md-5">
                          <div className="card-title">
                            <div className="d-flex align-items-center position-relative my-1">
                              <i className="ki-outline ki-calendar-tick fs-2 me-2 text-primary"></i>
                              <span className="fw-bold fs-5 text-gray-800">
                                📋 Records for {formatDateForDisplay(selectedDate)}
                              </span>
                              <span className="badge badge-light-primary ms-3 fs-6">
                                {filteredAds.length} {filteredAds.length === 1 ? 'Ad' : 'Ads'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="card-toolbar flex-row-fluid justify-content-end gap-5">
                            {/* Stats Cards */}
                            <div className="d-flex align-items-center gap-3">
                              <div className="border border-gray-300 border-dashed rounded min-w-100px py-3 px-4 stats-card">
                                <div className="fs-6 text-gray-800 fw-bold">{stats.paid}</div>
                                <div className="fw-semibold text-gray-400 fs-7">
                                  <i className="ki-outline ki-check-circle fs-7 text-success me-1"></i>
                                  Paid
                                </div>
                              </div>
                              <div className="border border-gray-300 border-dashed rounded min-w-100px py-3 px-4 stats-card">
                                <div className="fs-6 text-gray-800 fw-bold">{stats.pending}</div>
                                <div className="fw-semibold text-gray-400 fs-7">
                                  <i className="ki-outline ki-clock fs-7 text-warning me-1"></i>
                                  Pending
                                </div>
                              </div>
                              <div className="border border-gray-300 border-dashed rounded min-w-100px py-3 px-4 stats-card">
                                <div className="fs-6 text-gray-800 fw-bold">{stats.unsuccessful}</div>
                                <div className="fw-semibold text-gray-400 fs-7">
                                  <i className="ki-outline ki-cross-circle fs-7 text-danger me-1"></i>
                                  Unsuccessful
                                </div>
                              </div>
                              <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 stats-card" style={{ background: "linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)" }}>
                                <div className="fs-6 text-gray-800 fw-bold text-primary">{formatCurrency(stats.total)}</div>
                                <div className="fw-semibold text-gray-400 fs-7">
                                  <i className="ki-outline ki-money fs-7 text-primary me-1"></i>
                                  Total Value
                                </div>
                              </div>
                            </div>
                            
                            {/* Export Button */}
                            <button
                              type="button"
                              className="btn btn-light-success btn-sm"
                              onClick={handleExport}
                              disabled={exporting || filteredAds.length === 0}
                            >
                              {exporting ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <>
                                  <i className="ki-outline ki-exit-up fs-2"></i> 
                                  Export Day's Ads
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="card-body pt-0">
                          <div className="table-responsive">
                            <div style={{ minHeight: "400px" }}>
                              <table className="table align-middle table-row-dashed fs-6 gy-5">
                                <thead>
                                  <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0 bg-light">
                                    <th className="min-w-250px">Business Details</th>
                                    <th className="min-w-150px">Invoice</th>
                                    <th className="min-w-125px">Ad Type</th>
                                    <th className="min-w-125px">Zone</th>
                                    <th className="min-w-100px">Faces</th>
                                    <th className="min-w-125px">Amount</th>
                                    <th className="min-w-125px">Status</th>
                                    <th className="min-w-150px">Date Time</th>
                                    <th className="text-end min-w-200px">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="text-gray-600 fw-semibold">
                                  {calendarLoading ? (
                                    renderSkeletons()
                                  ) : (
                                    <>
                                      {filteredAds.length === 0 && (
                                        <tr>
                                          <td colSpan="9" className="text-center py-10">
                                            <div className="d-flex flex-column align-items-center">
                                              <i className="ki-outline ki-file-sheet fs-3x text-muted mb-3"></i>
                                              <div className="text-muted fs-6 fw-bold mb-2">
                                                No advertisements found for this date
                                              </div>
                                              <span className="text-muted fs-7">
                                                Try selecting another date or check back later
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                      {filteredAds.map((payment, index) => (
                                        <tr key={payment.id || index} className="border-bottom">
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div className="symbol symbol-50px me-3">
                                                <img
                                                  src={payment.ad?.imageUrl || "assets/bill2.jpg"}
                                                  alt="Ad"
                                                  className="rounded"
                                                  style={{
                                                    width: "50px",
                                                    height: "50px",
                                                    objectFit: "cover",
                                                    border: "2px solid #f0f0f0"
                                                  }}
                                                  onError={(e) => {
                                                    e.target.src = "assets/bill2.jpg";
                                                  }}
                                                />
                                              </div>
                                              <div className="d-flex flex-column">
                                                <Link
                                                  to="#"
                                                  className="text-gray-800 text-hover-primary fw-bold fs-6 mb-1"
                                                >
                                                  {getClientName(payment.ad?.client)}
                                                </Link>
                                                <span className="text-muted fw-semibold d-block fs-7">
                                                  {payment.ad?.title || "No Title"}
                                                </span>
                                                <span className="text-muted fw-semibold d-block fs-7">
                                                  {getClientEmail(payment.ad?.client)}
                                                </span>
                                              </div>
                                            </div>
                                          </td>
                                          <td>
                                            <span className="badge badge-light-primary fw-bold fs-7 px-3 py-2">
                                              {payment.code}
                                            </span>
                                          </td>
                                          <td>
                                            {toProperCase(payment.ad?.lookup?.type?.name || "-")}
                                          </td>
                                          <td>
                                            {toProperCase(payment.ad?.lookup?.location?.name || "-")}
                                          </td>
                                          <td>
                                            <span className="badge badge-light fw-bold">
                                              {payment.ad?.measurement || 1}x
                                            </span>
                                          </td>
                                          <td className="text-gray-800 fw-bold">
                                            {formatCurrency(payment.amount)}
                                          </td>
                                          <td>
                                            <div
                                              className={`badge ${
                                                payment.status === "Successful" 
                                                  ? "badge-light-success" 
                                                  : payment.status === "Initialize" || payment.status === "AwaitingPayment"
                                                    ? "badge-light-warning" 
                                                    : "badge-light-danger"
                                              } px-3 py-2`}
                                            >
                                              {payment.status === "Successful"
                                                ? "Paid"
                                                : payment.status === "Initialize"
                                                  ? "Pending"
                                                  : payment.status === "UnSuccessful"
                                                    ? "Unsuccessful"
                                                    : payment.status}
                                            </div>
                                          </td>
                                          <td>
                                            <div className="d-flex flex-column">
                                              <span className="text-gray-700 fw-bold">
                                                {new Date(payment.createdAt).toLocaleTimeString([], { 
                                                  hour: '2-digit', 
                                                  minute: '2-digit' 
                                                })}
                                              </span>
                                              <span className="text-muted fs-7">
                                                {new Date(payment.createdAt).toLocaleDateString('en-GB')}
                                              </span>
                                            </div>
                                          </td>
                                          <td className="text-end">
                                            {/* PAID - View Permit */}
                                            {payment.status === "Successful" && (
                                              <button
                                                className="btn btn-sm btn-light-success btn-active-success me-2"
                                                onClick={() => {
                                                  const agentFullName = payment.ad?.agent
                                                    ? `${payment.ad.agent.firstName || ""} ${payment.ad.agent.lastName || ""}`.trim()
                                                    : "Unassigned Agent";
                                                  setPermitData({
                                                    clientName: toProperCase(getClientName(payment.ad?.client)),
                                                    clientAddress: toProperCase(payment.ad?.client?.company?.address || "N/A"),
                                                    clientContact: payment.ad?.client?.company?.phoneNumber || 
                                                                   payment.ad?.client?.individual?.phoneNumber || "N/A",
                                                    amount: formatCurrency(payment.amount),
                                                    category: toProperCase(payment.ad?.lookup?.category?.name || "N/A"),
                                                    type: toProperCase(payment.ad?.lookup?.type?.name || "N/A"),
                                                    expiryDate: getExpiryDate(payment.paidAt),
                                                    adTitle: toProperCase(payment.ad?.title || ""),
                                                    adSize: toProperCase(payment.ad?.lookup?.size?.name || "N/A"),
                                                    adLocation: toProperCase(payment.ad?.location?.lga || "N/A"),
                                                    adAddress: toProperCase(payment.ad?.address || "N/A"),
                                                    imageUrl: payment.ad?.imageUrl || "assets/bill2.jpg",
                                                    status: "Paid",
                                                    paidAt: payment.paidAt,
                                                    agentName: toProperCase(agentFullName),
                                                    agentEmail: payment.ad?.agent?.email || "N/A",
                                                    agentPhone: payment.ad?.agent?.phoneNumber || "N/A",
                                                  });
                                                  setShowPermitModal(true);
                                                }}
                                              >
                                                <i className="ki-outline ki-file-certificate fs-6 me-1"></i>
                                                Permit
                                              </button>
                                            )}

                                            {/* PENDING - Edit & View */}
                                            {payment.status !== "Successful" && 
                                             payment.status !== "UnSuccessful" && (
                                              <>
                                                <button
                                                  className="btn btn-sm btn-light-primary btn-active-primary me-2"
                                                  onClick={() => handleEditClick(payment)}
                                                >
                                                  <i className="ki-outline ki-pencil fs-6 me-1"></i>
                                                  Edit
                                                </button>
                                                <button
                                                  className="btn btn-sm btn-light-info btn-active-info"
                                                  onClick={() => handleViewClick(payment)}
                                                >
                                                  <i className="ki-outline ki-eye fs-6 me-1"></i>
                                                  View
                                                </button>
                                              </>
                                            )}

                                            {/* UNSUCCESSFUL - View Only */}
                                            {payment.status === "UnSuccessful" && (
                                              <button
                                                className="btn btn-sm btn-light-info btn-active-info"
                                                onClick={() => handleViewClick(payment)}
                                              >
                                                <i className="ki-outline ki-eye fs-6 me-1"></i>
                                                Details
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          
                          {/* Summary Footer */}
                          {!calendarLoading && filteredAds.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center pt-4 border-top">
                              <div className="text-muted fs-7">
                                <i className="ki-outline ki-information fs-6 me-1"></i>
                                Showing all {filteredAds.length} advertisement{filteredAds.length !== 1 ? 's' : ''} for this day
                              </div>
                              <div className="text-muted fs-7 fw-bold">
                                Total Value: <span className="text-primary">{formatCurrency(stats.total)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========== EDIT MODAL ========== */}
        {showEditModal && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered mw-900px modal-dialog-scrollable">
              <div className="modal-content rounded shadow-lg">
                <div className="modal-header border-0 pb-0 position-relative">
                  <h3 className="modal-title fw-bold">
                    <i className="ki-outline ki-pencil fs-2 me-2 text-primary"></i>
                    Edit Advertisement
                  </h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon position-absolute top-0 end-0 m-3"
                    onClick={() => {
                      setShowEditModal(false);
                      setOriginalLookup(null);
                    }}
                    disabled={saving}
                  >
                    <i className="ki-outline ki-cross fs-1"></i>
                  </button>
                </div>

                <div className="modal-body py-5 px-lg-10">
                  <div className="row g-5">
                    {/* Basic Information */}
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-information fs-6 me-1"></i>
                        Basic Information
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Title <small className="text-muted">(Auto-generated)</small>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="title"
                            value={editForm.title}
                            onChange={handleEditFormChange}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Description</label>
                          <textarea
                            className="form-control"
                            name="description"
                            value={editForm.description}
                            onChange={handleEditFormChange}
                            rows="3"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Ad Image</label>
                          <div className="d-flex align-items-center gap-3">
                            {editForm.imagePreview && (
                              <div
                                className="symbol symbol-60px border rounded cursor-pointer"
                                onClick={() => {
                                  setLightboxImage(editForm.imagePreview);
                                  setShowLightbox(true);
                                }}
                              >
                                <img
                                  src={editForm.imagePreview}
                                  alt="Preview"
                                  className="object-fit-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </div>
                            )}
                            <input
                              type="file"
                              className="form-control"
                              name="imageFile"
                              accept="image/*"
                              onChange={handleEditFormChange}
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Address</label>
                          <textarea
                            className="form-control"
                            name="address"
                            value={editForm.address}
                            onChange={handleEditFormChange}
                            rows="2"
                          />
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                              Price (₦) <small className="text-muted">(Auto-calculated)</small>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">₦</span>
                              <input
                                type="text"
                                className={`form-control ${editForm.price === 0 ? "is-invalid" : ""}`}
                                value={editForm.price.toLocaleString("en-NG")}
                                readOnly
                              />
                            </div>
                            {editForm.price === 0 && (
                              <div className="text-danger small mt-1">
                                <i className="ki-outline ki-information fs-2 me-1"></i>
                                No pricing configuration found.
                              </div>
                            )}
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">Status</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editForm.status}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Advertisement Details */}
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-bill fs-6 me-1"></i>
                        Advertisement Details
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="row">
                          <div className="col-md-12 mb-3">
                            <label className="form-label fw-semibold">
                              Zone <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="lookup.location.name"
                              value={editForm.lookup.location.name}
                              onChange={handleEditFormChange}
                            >
                              <option value="">Select Zone</option>
                              {lookupOptions.zones.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label fw-semibold">
                              Size <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="lookup.size.name"
                              value={editForm.lookup.size.name}
                              onChange={handleEditFormChange}
                            >
                              <option value="">Select Size</option>
                              {lookupOptions.sizes.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                              Ad Type <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="lookup.type.name"
                              value={editForm.lookup.type.name}
                              onChange={handleEditFormChange}
                            >
                              <option value="">Select Ad Type</option>
                              {lookupOptions.types.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                              Party Type <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              name="lookup.category.name"
                              value={editForm.lookup.category.name}
                              onChange={handleEditFormChange}
                            >
                              <option value="">Select Party Type</option>
                              {lookupOptions.categories.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">No. of Faces</label>
                            <input
                              type="number"
                              className="form-control"
                              name="measurement"
                              value={editForm.measurement}
                              onChange={handleEditFormChange}
                              min="1"
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">Duration (Years)</label>
                            <input
                              type="number"
                              className="form-control"
                              name="time"
                              value={editForm.time}
                              onChange={handleEditFormChange}
                              min="1"
                            />
                          </div>
                        </div>
                        <div className={`alert ${editForm.price === 0 ? "alert-warning" : "alert-info"} mt-3 mb-0`}>
                          <small>
                            <i className={`ki-outline ki-${editForm.price === 0 ? "warning" : "information"} fs-2 me-2`}></i>
                            {editForm.price === 0
                              ? "Price will be calculated once all required fields are selected with valid combinations."
                              : `Price auto-calculated: ${formatCurrency(editForm.price)}`}
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-geolocation fs-6 me-1"></i>
                        Location Information
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">LGA</label>
                          <input
                            type="text"
                            className="form-control"
                            name="location.lga"
                            value={editForm.location.lga}
                            onChange={handleEditFormChange}
                          />
                        </div>
                        <div className="row">
                          <div className="col-md-12 mb-3">
                            <label className="form-label fw-semibold">Longitude</label>
                            <input
                              type="number"
                              step="any"
                              className="form-control"
                              name="location.longitude"
                              value={editForm.location.longitude}
                              onChange={handleEditFormChange}
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label fw-semibold">Latitude</label>
                            <input
                              type="number"
                              step="any"
                              className="form-control"
                              name="location.latitude"
                              value={editForm.location.latitude}
                              onChange={handleEditFormChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Client Information */}
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-profile-user fs-6 me-1"></i>
                        Client Information
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Client Type</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editForm.client.type}
                            readOnly
                          />
                        </div>
                        {editForm.client.type === "Company" ? (
                          <>
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Company Name</label>
                              <input
                                type="text"
                                className="form-control"
                                name="client.company.name"
                                value={editForm.client.company.name}
                                onChange={handleEditFormChange}
                              />
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Email</label>
                                <input
                                  type="email"
                                  className="form-control"
                                  name="client.company.email"
                                  value={editForm.client.company.email}
                                  onChange={handleEditFormChange}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Phone</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="client.company.phoneNumber"
                                  value={editForm.client.company.phoneNumber}
                                  onChange={handleEditFormChange}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">First Name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="client.individual.firstName"
                                  value={editForm.client.individual.firstName}
                                  onChange={handleEditFormChange}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Last Name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="client.individual.lastName"
                                  value={editForm.client.individual.lastName}
                                  onChange={handleEditFormChange}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Email</label>
                                <input
                                  type="email"
                                  className="form-control"
                                  name="client.individual.email"
                                  value={editForm.client.individual.email}
                                  onChange={handleEditFormChange}
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Phone</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="client.individual.phoneNumber"
                                  value={editForm.client.individual.phoneNumber}
                                  onChange={handleEditFormChange}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between mt-8">
                    <div>
                      <button
                        className="btn btn-danger btn-lg px-5"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={deleting}
                      >
                        {deleting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="ki-outline ki-trash fs-2 me-2"></i>
                            Delete Advertisement
                          </>
                        )}
                      </button>
                    </div>
                    <div className="d-flex gap-3">
                      <button
                        className="btn btn-light btn-lg px-5"
                        onClick={() => {
                          setShowEditModal(false);
                          setOriginalLookup(null);
                        }}
                        disabled={saving || deleting}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-lg px-5"
                        onClick={handleSaveAd}
                        disabled={saving || deleting || !editForm.lookUpId}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="ki-outline ki-save fs-2 me-2"></i>
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== DELETE CONFIRMATION MODAL ========== */}
        {showDeleteConfirm && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-danger">
                    <i className="ki-outline ki-trash fs-2 me-2"></i>
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="fs-6">
                    Are you sure you want to delete this advertisement? This action cannot be undone.
                  </p>
                  <p className="text-muted bg-light p-3 rounded">
                    <strong>Advertisement:</strong> {editForm.title}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteAd}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== PERMIT MODAL ========== */}
        {showPermitModal && permitData && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered mw-900px">
              <div className="modal-content rounded shadow-lg">
                <div className="modal-header border-0 pb-0 position-relative">
                  <div className="w-100 text-center mb-2">
                    <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                      <img src="assets/edo.png" alt="Edo Logo" className="h-60px" />
                      <img src="assets/edsal.png" alt="EDSAL Logo" className="h-60px" />
                    </div>
                    <h3 className="fw-bold text-uppercase text-success mb-1">
                      Edo State Signage And Advertisement Agency
                    </h3>
                    <div className="separator separator-dashed my-3"></div>
                    <h4 className="fw-bold text-uppercase text-success mb-0">
                      Advertising Permit
                    </h4>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon position-absolute top-0 end-0 m-3"
                    onClick={() => setShowPermitModal(false)}
                  >
                    <i className="ki-outline ki-cross fs-1"></i>
                  </button>
                </div>
                <div className="modal-body py-5 px-lg-10">
                  <div className="d-flex flex-column align-items-center mb-8">
                    <div
                      className="rounded overflow-hidden shadow-sm border w-100 bg-light d-flex justify-content-center align-items-center"
                      style={{ minHeight: "300px" }}
                    >
                      <img
                        src={permitData.imageUrl}
                        alt="Ad Preview"
                        className="img-fluid img-zoom-hover cursor-pointer"
                        style={{ maxHeight: "400px", objectFit: "contain" }}
                        onError={(e) => {
                          e.target.src = "assets/bill2.jpg";
                        }}
                        onClick={() => {
                          setLightboxImage(permitData.imageUrl);
                          setShowLightbox(true);
                        }}
                      />
                    </div>
                  </div>
                  <div className="row g-5">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-profile-user fs-6 me-1"></i>
                        Client Information
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2">
                          <strong>Name:</strong> {permitData.clientName}
                        </div>
                        <div className="mb-2">
                          <strong>Address:</strong> {permitData.clientAddress}
                        </div>
                        <div className="mb-0">
                          <strong>Contact:</strong> {permitData.clientContact}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-file-certificate fs-6 me-1"></i>
                        Permit Details
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2">
                          <strong>Amount:</strong> {permitData.amount}
                        </div>
                        <div className="mb-2">
                          <strong>Category:</strong> {permitData.category}
                        </div>
                        <div className="mb-2">
                          <strong>Type:</strong> {permitData.type}
                        </div>
                        <div className="mb-0">
                          <strong>Expiry:</strong> {permitData.expiryDate}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-bill fs-6 me-1"></i>
                        Advertisement Details
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2">
                          <strong>Title:</strong> {permitData.adTitle}
                        </div>
                        <div className="mb-2">
                          <strong>Size:</strong> {permitData.adSize}
                        </div>
                        <div className="mb-2">
                          <strong>Location:</strong> {permitData.adLocation}
                        </div>
                        <div className="mb-0">
                          <strong>Address:</strong> {permitData.adAddress}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-user fs-6 me-1"></i>
                        Agent Information
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="mb-2">
                          <strong>Name:</strong> {permitData.agentName}
                        </div>
                        <div className="mb-2">
                          <strong>Email:</strong> {permitData.agentEmail}
                        </div>
                        <div className="mb-0">
                          <strong>Phone:</strong> {permitData.agentPhone}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-8">
                    <button className="btn btn-success btn-lg px-5">
                      <i className="ki-outline ki-file-down fs-2 me-2"></i>
                      Download Permit
                    </button>
                    <button
                      className="btn btn-light btn-lg px-5"
                      onClick={() => setShowPermitModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== VIEW DETAILS MODAL ========== */}
        {showViewModal && viewData && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered mw-800px">
              <div className="modal-content rounded shadow-lg border-0">
                <div className="modal-header pb-0 border-0">
                  <h3 className="modal-title fw-bold">
                    <i className="ki-outline ki-eye fs-2 me-2 text-primary"></i>
                    Advertisement Details
                  </h3>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowViewModal(false)}
                  ></button>
                </div>
                <div className="modal-body py-5 px-lg-10">
                  <div className="d-flex flex-column align-items-center mb-5">
                    <div
                      className="rounded overflow-hidden shadow-sm border w-100 bg-light d-flex justify-content-center align-items-center"
                      style={{ maxHeight: "300px" }}
                    >
                      <img
                        src={viewData.imageUrl}
                        alt="Ad"
                        className="img-fluid img-zoom-hover cursor-pointer"
                        style={{ maxHeight: "300px", cursor: "pointer" }}
                        onError={(e) => {
                          e.target.src = "assets/bill2.jpg";
                        }}
                        onClick={() => {
                          setLightboxImage(viewData.imageUrl);
                          setShowLightbox(true);
                        }}
                      />
                    </div>
                  </div>
                  <div className="row g-5">
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-wallet fs-6 me-1"></i>
                        Payment Info
                      </h6>
                      <div className="bg-light p-4 rounded border h-100">
                        <div className="mb-2">
                          <span className="fw-semibold">Invoice:</span> {viewData.invoiceCode}
                        </div>
                        <div className="mb-2">
                          <span className="fw-semibold">Amount:</span> {formatCurrency(viewData.amount)}
                        </div>
                        <div className="mb-2">
                          <span className="fw-semibold">Status:</span>{" "}
                          <span className={`badge ${
                            viewData.status === "Successful" ? "badge-light-success" : 
                            viewData.status === "Initialize" ? "badge-light-warning" : "badge-light-danger"
                          }`}>
                            {viewData.status}
                          </span>
                        </div>
                        <div>
                          <span className="fw-semibold">Date:</span> {formatDate(viewData.date)}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-profile-user fs-6 me-1"></i>
                        Client Info
                      </h6>
                      <div className="bg-light p-4 rounded border h-100">
                        <div className="mb-2">
                          <span className="fw-semibold">Name:</span> {toProperCase(viewData.clientName)}
                        </div>
                        <div className="mb-2">
                          <span className="fw-semibold">Phone:</span> {viewData.clientPhone}
                        </div>
                        <div>
                          <span className="fw-semibold">Email:</span> {viewData.clientEmail}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <h6 className="fw-bold text-gray-700 mb-3">
                        <i className="ki-outline ki-bill fs-6 me-1"></i>
                        Advertisement Info
                      </h6>
                      <div className="bg-light p-4 rounded border">
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <span className="fw-semibold">Title:</span> {toProperCase(viewData.adTitle)}
                          </div>
                          <div className="col-md-6 mb-2">
                            <span className="fw-semibold">Zone:</span> {toProperCase(viewData.adZone)}
                          </div>
                          <div className="col-md-6 mb-2">
                            <span className="fw-semibold">Type:</span> {toProperCase(viewData.adType)}
                          </div>
                          <div className="col-md-6 mb-2">
                            <span className="fw-semibold">Size:</span> {viewData.adSize}
                          </div>
                          <div className="col-12">
                            <span className="fw-semibold">Address:</span> {toProperCase(viewData.adAddress)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-5">
                    <button
                      className="btn btn-light"
                      onClick={() => setShowViewModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== LIGHTBOX ========== */}
        {showLightbox && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.95)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "zoom-out",
            }}
            onClick={() => setShowLightbox(false)}
          >
            <div className="position-absolute top-0 end-0 m-4">
              <button
                className="btn btn-icon btn-sm btn-active-light-primary"
                onClick={() => setShowLightbox(false)}
              >
                <i className="ki-outline ki-cross fs-2x text-white"></i>
              </button>
            </div>
            <img
              src={lightboxImage}
              alt="Full Screen"
              style={{
                maxHeight: "90vh",
                maxWidth: "90vw",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 0 30px rgba(0,0,0,0.7)",
              }}
              onError={(e) => {
                e.target.src = "assets/bill2.jpg";
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CalendarAdsManagement;